import type { V2 } from '../core/math';
import type { BodyTagData, EntityKind } from '../core/events';

/**
 * Physics2D: deterministic arcade rigid-body physics on the XZ ground plane.
 * Units: metres, kilograms, seconds. Angles follow the heading convention in core/math.ts.
 */

/** Collision layer bits. A pair collides when (a.layer & b.mask) && (b.layer & a.mask). */
export const Layer = {
  STATIC: 1 << 0,
  VEHICLE: 1 << 1,
  PED: 1 << 2,
  PLAYER: 1 << 3,
  PROP: 1 << 4,
  /** Debris / small things that only collide with statics. */
  DEBRIS: 1 << 5,
  /**
   * Online: the car this client drives (on top of VEHICLE). The one local car a server police unit's
   * proxy collides with: our ambient traffic is ours alone (src/net/proxyMotion.ts unitProxyMask).
   */
  NET_SELF: 1 << 6,
  ALL: 0xffff,
} as const;

/** Box half extents: `hx` along local X (width/2), `hz` along local Z (length/2, the forward axis). */
export interface BoxShape {
  type: 'box';
  hx: number;
  hz: number;
}
export interface CircleShape {
  type: 'circle';
  r: number;
}
export type Shape = BoxShape | CircleShape;

export interface BodyDef {
  shape: Shape;
  pos: V2;
  angle?: number;
  /** Mass in kg (ignored for kinematic bodies). */
  mass?: number;
  /** Multiplier on the computed moment of inertia (>1 = more resistant to spinning). */
  inertiaScale?: number;
  restitution?: number;
  friction?: number;
  /** Velocity damping per second (simple exponential). */
  linearDamping?: number;
  angularDamping?: number;
  layer?: number;
  mask?: number;
  /** 'kinematic' = infinite mass, moved only by its velocity. */
  type?: 'dynamic' | 'kinematic';
  /** Identifies the owning entity in collision events. */
  kind?: EntityKind;
  entityId?: number;
  canSleep?: boolean;
  userData?: unknown;
}

/** Static world geometry. Segments become thin boxes internally. */
export type StaticDef =
  | {
      kind: 'box';
      center: V2;
      halfX: number;
      halfZ: number;
      angle?: number;
      /** Blocks police line-of-sight (default true). Low walls / fences can set false. */
      blocksSight?: boolean;
      friction?: number;
      restitution?: number;
      tag?: string;
    }
  | {
      kind: 'segment';
      a: V2;
      b: V2;
      /** Wall thickness in metres (default 0.6; keep >= 0.4 to avoid tunnelling). */
      thickness?: number;
      blocksSight?: boolean;
      friction?: number;
      restitution?: number;
      tag?: string;
    }
  | {
      kind: 'circle';
      center: V2;
      radius: number;
      blocksSight?: boolean;
      friction?: number;
      restitution?: number;
      tag?: string;
    };

export interface TriggerDef {
  shape: Shape;
  pos: V2;
  angle?: number;
  /** Which body layers can trigger it (default VEHICLE | PLAYER). */
  mask?: number;
  /** Free-form tag, e.g. 'mission:dropoff'. Delivered with events. */
  tag: string;
}

export interface RaycastOptions {
  /** Dynamic body layers to test (default ALL). Use 0 to test statics only. */
  mask?: number;
  includeStatics?: boolean;
  /** Only statics with blocksSight = true count. */
  sightOnly?: boolean;
  /** Body to ignore (usually the caster). */
  ignore?: { id: number } | null;
}

export interface RayHit {
  point: V2;
  normal: V2;
  distance: number;
  /** Dynamic body hit, or null if a static collider was hit. */
  bodyId: number | null;
  staticId: number | null;
  tag: BodyTagData | null;
  /** The static's free-form tag from its StaticDef ('building:12', 'wall:railing', 'prop:lamp'); null for bodies. */
  label: string | null;
}

/** Aggregated contact info for one touching pair during one fixed step. */
export interface ContactEvent {
  a: BodyTagData;
  b: BodyTagData | null;
  /** Total normal impulse applied this step (N·s). */
  impulse: number;
  /** Closing speed along the normal at first contact this step (m/s, >= 0). */
  speed: number;
  /** Max tangential (sliding) speed at the contact this step (m/s) — scrapes, sparks. */
  slide: number;
  pos: V2;
  /** Normal from a to b. */
  normal: V2;
  isNew: boolean;
}

export interface TriggerEvent {
  type: 'enter' | 'exit';
  triggerId: number;
  triggerTag: string;
  body: BodyTagData;
}

export interface PhysicsStats {
  bodies: number;
  awake: number;
  statics: number;
  pairsTested: number;
  contacts: number;
  stepMs: number;
}
